/**
 * ModuleCards - Custom SurveyJS renderer for bootcamp module selection
 * Renders radio choices as rich cards with descriptions, pricing, and included items
 */
import React from 'react';
import { ItemValue, RendererFactory, Serializer } from 'survey-core';
import { ReactQuestionFactory, SurveyQuestionRadiogroup } from 'survey-react-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCalendar, faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import style from './_index.module.scss';

// Extended item type for module cards
interface ModuleItemValue extends ItemValue {
  description?: string;
  duration?: string;
  priceDisplay?: string;
  isFree?: boolean;
  includedItems?: string[];
  additionalInfo?: string;
}

export class ModuleCardsQuestion extends SurveyQuestionRadiogroup {
  constructor(props: any) {
    super(props);

    // Override renderItem for card-based rendering
    (this as any).renderItem = (
      item: ModuleItemValue,
      value: any,
      cssClasses: any,
      index?: string
    ) => {
      const inputId = `input_${this.question.name}_${item.id}`;
      const isChecked = this.question.isItemSelected(item);

      const handleOnChange = () => {
        this.question.clickItemHandler(item);
      };

      const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.question.clickItemHandler(item);
        }
      };

      // Get custom properties from item
      const duration = item.getPropertyValue('duration') || '';
      const priceDisplay = item.getPropertyValue('priceDisplay') || 'FREE';
      const priceSuperscript = item.getPropertyValue('priceSuperscript') || '';
      const includedItems: string[] = item.getPropertyValue('includedItems') || [];
      const additionalInfo = item.getPropertyValue('additionalInfo') || '';
      const originalPriceDisplay = item.getPropertyValue('originalPriceDisplay') || '';
      const upgradePriceDisplay = item.getPropertyValue('upgradePriceDisplay') || '';

      const cardClasses = [style.module_card];
      if (isChecked) {
        cardClasses.push(style.module_card_active);
      }

      return (
        <div
          role="radio"
          aria-checked={isChecked}
          tabIndex={0}
          className={cardClasses.join(' ')}
          onClick={handleOnChange}
          onKeyDown={handleKeyDown}
          key={item.id}
          id={inputId}
        >
          <input
            type="radio"
            id={this.question.getItemId(item)}
            disabled={!this.question.getItemEnabled(item)}
            name={this.question.name}
            value={item.value}
            checked={isChecked}
            onChange={handleOnChange}
            className={style.hidden_radio}
          />

          <div className={style.card_header}>
            <h3
              className={style.card_title}
              dangerouslySetInnerHTML={{ __html: item.text }}
            />
            <div className={style.card_price_wrapper}>
              {originalPriceDisplay ? (
                <div className={style.upgrade_price_container}>
                  <span className={style.original_price}>{originalPriceDisplay}</span>
                  <span className={style.price_amount}>{priceDisplay}</span>
                  <span className={style.upgrade_label}>{upgradePriceDisplay}</span>
                </div>
              ) : (
                <>
                  <span className={style.price_amount}>
                    {priceDisplay}
                  </span>
                  {priceSuperscript && (
                    <sup className={style.price_superscript}>{priceSuperscript}</sup>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={style.card_duration}>
            <FontAwesomeIcon icon={faCalendar as IconProp} className={style.duration_icon} />
            <span dangerouslySetInnerHTML={{ __html: duration }} />
          </div>

          {additionalInfo && (
            <p
              className={`${style.card_additional_info} ${style.card_additional_info_paid}`}
              dangerouslySetInnerHTML={{ __html: additionalInfo }}
            />
          )}

          {includedItems.length > 0 && (
            <div className={style.included_section}>
              <span className={style.included_label}>What's Included:</span>
              <ul className={style.included_list}>
                {includedItems.map((includedItem, idx) => (
                  <li key={idx} className={style.included_item}>
                    <FontAwesomeIcon icon={faCircleCheck as IconProp} className={style.check_icon} />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: typeof includedItem === 'string' ? includedItem : (includedItem as any).text || String(includedItem)
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={style.card_selection_indicator}>
            <span className={style.selection_circle}>
              {isChecked && <span className={style.selection_dot}></span>}
            </span>
            <span className={style.selection_text}>
              {isChecked ? 'Selected' : 'Select'}
            </span>
          </div>
        </div>
      );
    };
  }

  // Override getBody for grid layout
  getBody(cssClasses: any) {
    return (
      <div className={style.module_cards_container}>
        {this.getItems(cssClasses, this.question.bodyItems)}
      </div>
    );
  }
}

// Add custom properties to itemvalue for module cards
Serializer.addProperty('itemvalue', {
  name: 'description',
  displayName: 'Description',
  type: 'text',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'duration',
  displayName: 'Duration',
  type: 'string',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'priceDisplay',
  displayName: 'Price Display',
  type: 'string',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'priceSuperscript',
  displayName: 'Price Superscript',
  type: 'string',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'isFree',
  displayName: 'Is Free',
  type: 'boolean',
  default: false,
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'includedItems',
  displayName: 'Included Items',
  type: 'string[]',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'additionalInfo',
  displayName: 'Additional Info',
  type: 'text',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'originalPriceDisplay',
  displayName: 'Original Price Display',
  type: 'string',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

Serializer.addProperty('itemvalue', {
  name: 'upgradePriceDisplay',
  displayName: 'Upgrade Price Display',
  type: 'string',
  visibleIf: (obj) => obj.locOwner?.renderAs === 'modulecards',
});

// Register the React component
ReactQuestionFactory.Instance.registerQuestion(
  'sv-module-cards',
  (props) => {
    return React.createElement(ModuleCardsQuestion, props);
  }
);

// Register the renderer
RendererFactory.Instance.registerRenderer(
  'radiogroup',
  'modulecards',
  'sv-module-cards'
);

export default ModuleCardsQuestion;
